// Shared queue panel for the lobby — Ranked (auth + rating) and Quickplay
// (anonymous) tabs. Split out of LobbyScreen.tsx.

import { Button } from '@aod/ui/components/ui/button';
import { useCallback, useEffect, useState } from 'react';
import { getGatewayHttpUrl, resolveGatewayHttpUrl } from '../../auth/serviceToken';
import { useSession } from '../../auth/useSession';
import type { PlayerRating } from '../ratingApi';
import { fetchRating } from '../ratingApi';
import type { UseMpLobbyReturn } from './useMpLobby';

export const AI_DIFFICULTIES: Array<'easy' | 'normal' | 'hard'> = ['easy', 'normal', 'hard'];
/** Slice 1 queues exactly this format; more arrive with per-format configs. */
const RANKED_FORMAT = 'blitz_1v1';

/** Shared queue panel for Ranked (auth required, moves rating) and
 *  Quickplay (anonymous, no rating change). */
export function QueuePanel({
  mp,
  ranked,
  localPlayerName,
  gatewayUrl,
}: {
  mp: UseMpLobbyReturn;
  ranked: boolean;
  localPlayerName: string;
  gatewayUrl?: string;
}) {
  const session = useSession();
  const [rating, setRating] = useState<PlayerRating | null>(null);

  const signedIn = session.state.status === 'signed-in';
  const sub = signedIn ? session.state.user.id : null;

  useEffect(() => {
    if (!ranked || !sub) return;
    const gwHttp = getGatewayHttpUrl() ?? resolveGatewayHttpUrl(gatewayUrl ?? '');
    if (!gwHttp) return;
    let cancelled = false;
    void fetchRating(gwHttp, sub).then((r) => {
      if (!cancelled) setRating(r);
    });
    return () => {
      cancelled = true;
    };
  }, [ranked, sub, gatewayUrl]);

  if (ranked && !signedIn) {
    return (
      <div className="flex flex-col items-start gap-3">
        <p className="text-sm text-amber-100/80">Sign in to play ranked — your games count.</p>
        <Button type="button" size="lg" onClick={session.signIn} disabled={!session.configured}>
          Sign in
        </Button>
        {!session.configured && (
          <p className="text-xs text-amber-100/70" data-testid="ranked-unavailable">
            Ranked sign-in is not available in this build. Quickplay and Custom games need no
            account.
          </p>
        )}
      </div>
    );
  }

  const queued = mp.queueStatus !== null;
  return (
    <div className="flex flex-col items-start gap-3">
      {/* The muster while you wait (art audit P5): the menu pool, wire only. */}
      <img
        src="/assets/images/ui/menu_hero_3.webp"
        alt="An ancient druidic forest with rune-marked temples"
        loading="lazy"
        className="h-20 w-full rounded-lg border border-amber-200/20 object-cover"
        data-testid="queue-hero"
      />
      {ranked && rating && (
        <p className="text-sm text-amber-100" data-testid="ranked-rating">
          <span className="capitalize">{rating.league}</span> — {Math.round(rating.rating)} MMR (
          {rating.wins}W / {rating.losses}L)
        </p>
      )}
      {ranked && signedIn && !rating && (
        <p className="text-sm text-amber-100/80">Loading rating…</p>
      )}
      {queued ? (
        <>
          <p className="text-sm text-amber-200" aria-live="polite" data-testid="queue-wait">
            Searching {mp.queueStatus?.format ?? RANKED_FORMAT}…
            {mp.queueStatus && mp.queueStatus.waitMs > 0
              ? ` (${Math.round(mp.queueStatus.waitMs / 1000)}s)`
              : ''}
          </p>
          <Button type="button" variant="destructive" size="lg" onClick={mp.leaveQueue}>
            Cancel
          </Button>
        </>
      ) : (
        <>
          <Button
            type="button"
            size="lg"
            onClick={() => mp.joinQueue(RANKED_FORMAT, ranked, localPlayerName.trim() || 'Player')}
            disabled={mp.queuePending || (!ranked && !localPlayerName.trim())}
            data-testid={ranked ? 'queue-ranked' : 'queue-quickplay'}
          >
            {ranked ? 'Find Ranked Match' : 'Find Quickplay Match'}
          </Button>
          {!ranked && !localPlayerName.trim() && (
            <p className="text-xs text-amber-100/70">Enter a name above to queue.</p>
          )}
          {mp.queueError && (
            <p className="text-sm text-red-300" role="alert" data-testid="queue-error">
              {mp.queueError.message}
            </p>
          )}
          {!ranked && (
            <p className="text-xs text-amber-100/70">Quickplay never changes your rating.</p>
          )}
        </>
      )}
    </div>
  );
}
